/**
 * THE ARTWORK STUDIO IS ONE TOOL — pure, no React, no DOM.
 *
 * Brian: *"the artwork studio does not look like a refined tool there are 4
 * different tabs."*
 *
 * ⚠️ IT WAS WORSE THAN FOUR. Measured on the live site, 2026-09-09: the page
 * rendered `1 · Build | 2 · Preview & assemble | Marks Library | Elements`, and
 * the Builder inside it rendered a SECOND bar, `Builder | Build history`. Six
 * destinations across two stacked tab bars, and on a 375px viewport the first
 * screenful was a five-line lead paragraph and both bars — no work at all.
 *
 * Two of those four were never stages of building anything:
 *   Marks Library  an asset-management surface (find a mark, work the
 *                  propose → approve / reject queue)
 *   Elements       a read-only Canva browser
 * Both are SOURCES that feed a step, and the Builder already has a crest picker
 * on step 1 and folder-scoped element pickers on steps 2/5/6/13. So as peers
 * they were duplicating capability that already existed inside the flow, while
 * pushing the flow itself off the screen.
 *
 * WHAT A REP ACTUALLY DOES: builds a piece, then hands it on. That is TWO
 * stages, and this file is the model of them. It was three until 2026-09-09,
 * when "Preview & assemble" was retired on the evidence — see `RETIRED_STAGES`
 * below, which states what it failed to answer and where its machinery went.
 *
 * ⚠️ NOTHING BECOMES UNREACHABLE. A tab that stops being a tab has to keep its
 * capability somewhere findable, so every retired tab declares the STEPS it now
 * opens from and the capability that made it worth keeping. `verify-studio.mjs`
 * asserts that every retired tab has at least one home step and that every home
 * step is a real step key — the deletion cannot outrun the relocation.
 */

/* ------------------------------------------------------------------ *
 * The three stages
 * ------------------------------------------------------------------ */

export const STAGE_BUILD = 'build'
export const STAGE_HANDOFF = 'handoff'

export const STAGES = [
  {
    id: STAGE_BUILD,
    n: 1,
    label: 'Build',
    short: 'Build',
    what: 'Pick the job. Sourced build answers thirteen steps; Generated concept takes one line and sources the rest.',
  },
  {
    id: STAGE_HANDOFF,
    n: 2,
    label: 'Hand off',
    short: 'Hand off',
    what: 'Every build you have made, and the human-gated ladder: draft → proof → submitted → production.',
  },
]

/* ------------------------------------------------------------------ *
 * The stage this build retired
 * ------------------------------------------------------------------ */

/**
 * ⚠️ "PREVIEW & ASSEMBLE" IS RETIRED AS A STAGE, ON THE EVIDENCE.
 *
 * Brian: *"I truly do not know why we have a preview and assemble screen right
 * now"* — and the reason he could not tell is that it did not do the thing its
 * name promised. Measured in the code, 2026-09-09:
 *
 *   `ArtworkStudio.jsx`  rendered  `<Composer school={school} />`
 *
 * ONE PROP, and `school` came from a SECOND SchoolPicker in the preview stage's
 * own left rail. The Builder's build state was never passed. `Composer.jsx`
 * holds zero references to `studio/`, seeds its own inputs from
 * `seedFromSchool()`, keeps its own material list, and writes its own
 * differently-shaped record. So stage 2 did not preview stage 1 — it was a
 * second, disconnected builder, and level 1's *"Open stage 2 — preview &
 * assemble"* CTA sent a rep who had just answered thirteen steps to an empty
 * one where the first task was to pick their school again.
 *
 * It also never touched the render path. The Builder posts to
 * `POST /api/artwork/render` and the engine assembles the prompts and the
 * images SERVER-side; the Composer's three.js viewer is client-side and is not
 * consulted. Nothing a rep did in stage 2 reached the build they had made.
 *
 * THE DECAL PROJECTION IS REAL AND IT STAYS. `DecalGeometry`, `captureMask()`
 * and the photoreal pass are genuine machinery — the mask is what protects the
 * crest and the keyed type from the finish pass. MACHINERY DOES NOT NEED TO BE
 * A DESTINATION. `Composer.jsx` and `engine/` remain in the repo, unrouted,
 * exactly as `ConceptGenerator.jsx` does, and `verify-artwork.mjs` still pins
 * the mask-required refusal and the no-prompt-field payload.
 */
export const RETIRED_STAGES = [
  {
    id: 'preview',
    label: 'Preview & assemble',
    /* The question a stage has to be able to answer to keep its place: what
     * does a rep do here that they cannot do elsewhere? This one could not
     * answer it, because it never received the build. */
    why: 'it never received the build — it took only a school, from its own second picker, and shared no state with the Builder',
    machinery: 'the decal projection, captureMask() and the photoreal pass stay as machinery in the render path; Composer.jsx stays in the repo, unrouted',
  },
]

export const RETIRED_STAGE_IDS = RETIRED_STAGES.map((s) => s.id)

export const STAGE_IDS = STAGES.map((s) => s.id)
export const STAGE_BY_ID = Object.fromEntries(STAGES.map((s) => [s.id, s]))

/**
 * Read `?stage=` off a query string. An unknown or absent value lands on
 * BUILD — the stage a rep starts in — rather than on nothing.
 */
export function stageFromSearch(search) {
  let want = ''
  try {
    want = new URLSearchParams(String(search || '')).get('stage') || ''
  } catch {
    return STAGE_BUILD
  }
  return STAGE_IDS.includes(want) ? want : STAGE_BUILD
}

/* ------------------------------------------------------------------ *
 * The sources — the two former tabs, and where they went
 * ------------------------------------------------------------------ */

/**
 * A SOURCE feeds a step. It is not a destination, so it is not on the stage
 * rail; it opens, collapsed by default, inside the step that needs it.
 *
 * `unique` is the reason each one still exists as its own panel rather than
 * being deleted into the inline picker. Where a source adds nothing the inline
 * picker lacks, it should be deleted, not relocated — so this field has to be
 * answerable for every entry, and the verify suite checks that it is.
 */
export const SOURCES = [
  {
    id: 'marks',
    label: 'Marks Library',
    /* The inline `LogoCPicker` finds an owned mark, uploads one and attaches a
     * reference. What it cannot do is REVIEW: the propose → approve / reject
     * queue is the Marks Library's own, and approving is what promotes a staged
     * `-proposed` file to the live `-primary` mark every other build then
     * finds. Deleting that would quietly stop the owned-mark pipeline. */
    unique: 'the propose → approve / reject queue — approving is what promotes a staged mark to the live -primary file',
    open: 'Review proposed marks',
    stepKeys: ['logoC'],
    wasTab: true,
  },
  {
    id: 'elements',
    label: 'Elements',
    /* Each element step opens on ONE folder and searches only that subtree —
     * which is right when you know you want a bezel. It is wrong when you are
     * looking for something and do not yet know where it lives. The Elements
     * browser starts at the SR Artwork Engine ROOT and navigates the whole
     * library, so it stays. */
    unique: 'browsing the whole SR Artwork Engine library from its root, unscoped by a step',
    open: 'Browse the whole SR library',
    stepKeys: ['bezel', 'shank1', 'shank2', 'background'],
    wasTab: true,
  },
]

export const SOURCE_BY_ID = Object.fromEntries(SOURCES.map((s) => [s.id, s]))

/** The tabs this build retired. Every one must be reachable from a step. */
export const RETIRED_TABS = SOURCES.filter((s) => s.wasTab).map((s) => s.id)

/** The sources that open from this step, in declared order. */
export function sourcesForStep(stepKey) {
  const key = String(stepKey || '')
  return SOURCES.filter((s) => s.stepKeys.includes(key))
}

/** Every step key any source claims a home on. */
export function sourceHomeKeys() {
  const out = new Set()
  for (const s of SOURCES) for (const k of s.stepKeys) out.add(k)
  return [...out]
}
